// Atalho de horas no cabeçalho.
//
// A tela de horas existe, mas ir até ela é um passo — e passo é o que faz a
// pessoa deixar para depois (e "depois" é o motivo da cobertura estar em
// 41-85%). Aqui ela lança o dia de onde estiver, em três interações: abrir,
// escolher o contrato, digitar as horas.
//
// O ponto laranja ao lado do relógio é a cobrança silenciosa: aparece enquanto
// o dia de hoje está zerado e some assim que há alguma hora (ou é ausência).
// Não existe mais "fechar o dia" (01/10/2026, pedido do Lucas: "quanto mais
// limpo, melhor") — dia completo é o que tem as horas, não o que foi fechado.
//
// v2 (23/09/2026): CRONÔMETRO. Ligado, o botão mostra o tempo correndo
// ("● 0:42"); esquecido desde ontem, fica laranja. Regras que a revisão do
// desenho cravou:
//   · o bloco do cronômetro aparece SEMPRE — com o dia marcado como ausência
//     também (senão o Parar some e o ponto segue correndo);
//   · toda ação manda o id do cronômetro que a tela está vendo; se outra aba
//     mudou, o servidor recusa e aqui se relê — e relê também ao voltar o
//     foco para a aba, além dos 10 min;
//   · esquecido ligado, o Parar pergunta "quando você parou?" com DIA e hora;
//   · "Trocar a partir de agora" (começa outro no mesmo minuto) e "Corrigir
//     desde 08:12" (muda o contrato do período inteiro) são ações separadas;
//   · o tempo correndo sai do relógio do SERVIDOR, não do computador.
//
// HTML/CSS inline, sem antd: é assim que o NotificacoesBell vizinho é feito, e
// o cabeçalho tem que parecer um só.
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import {
  parseEntradaHoras, formatarHoras, segundaDe, estadoDoDia, parseHorario, formatarHorario, duracaoEntre,
  decorrido, formatarRelogio, diaDaSemana, perguntaDeSoma, segundaDeIso, agoraSP, type MarcaLocal,
} from '../pages/operacional-horas/horasUtils';
import AtividadeCampo, { type AtividadeTipo } from '../pages/operacional-horas/AtividadeCampo';
import DestinoCampo from '../pages/operacional-horas/DestinoCampo';
import TotalHoras from '../pages/operacional-horas/TotalHoras';
import { montarOpcoesDestino } from '../pages/operacional-horas/destinos';
import dayjs from 'dayjs';

const AZUL = '#0930D0';
const LARANJA = '#E8890C';
const VERMELHO = '#D93025';

type Ap = {
  id: string; data: string; minutos: number;
  contrato_id: string | null; centro_custo_codigo: string | null;
  contrato_numero: string | null; centro_custo_nome: string | null;
  hora_inicio?: string | null; hora_fim?: string | null;
};
type Dia = {
  data: string; status: string; tipo_ausencia: string | null; minutos_total: number;
  remoto?: boolean; campo?: boolean;   // home office / campo do dia (30/09/2026)
};
type Contrato = { id: string; numero: string; titulo: string; meu: boolean };
type CC = { codigo_canonical: string; nome_completo: string };
type Cron = {
  id: string; inicio: string; data_inicio: string; hora_inicio: string;
  contrato_id: string | null; centro_custo_codigo: string | null;
  contrato_numero: string | null; centro_custo_nome: string | null;
  atividade_tipo_id: number | null; atividade_tipo_nome: string | null; descricao: string | null;
};
type PerguntaFim = { texto: string; data: string; hora: string; confirmavel: boolean };

class ErroReq extends Error {
  dados: any;
  constructor(msg: string, dados: any) { super(msg); this.dados = dados; }
}

async function req(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') {
    try { d = JSON.parse(d.body); } catch { /* body não era JSON */ }
  }
  if (!r.ok || d?.erro || d?.error) throw new ErroReq(d?.erro || d?.error || `Erro ${r.status}`, d);
  return d;
}

// A tela da semana e o atalho se avisam quando um muda horas ou o cronômetro
// (senão a semana aberta mostra um cronômetro que já parou, e a pergunta de
// "hora contada duas vezes" não enxerga o que o outro gravou).
export const EVENTO_HORAS = 'horas:mudou';
// O pop-up de bom-dia ("Escolher outro") pede para o atalho se abrir.
export const EVENTO_ABRIR_ATALHO = 'horas:abrir-atalho';
const avisarMudanca = () => { try { window.dispatchEvent(new Event(EVENTO_HORAS)); } catch { /* sem window */ } };

// Último destino/atividade usados: o "▶ Continuar" de um toque.
const CHAVE_ULTIMO = 'horas.ultimo';
type Ultimo = { destino: string; atividade: number | null; texto: string; rotulo: string };
function lerUltimo(): Ultimo | null {
  try { const v = window.localStorage.getItem(CHAVE_ULTIMO); return v ? JSON.parse(v) : null; } catch { return null; }
}
function gravarUltimo(u: Ultimo) {
  try { window.localStorage.setItem(CHAVE_ULTIMO, JSON.stringify(u)); } catch { /* sem armazenamento */ }
}

// O que está meio preenchido no atalho sobrevive a fechar o painel, trocar de
// tela e recarregar a página (pedido do Lucas, 28/09: "pelo menos não reseta o
// que eu já preenchi"). Vale só para o mesmo dia; lançar ou ligar o
// cronômetro limpa, como antes.
const CHAVE_RASCUNHO = 'horas.rascunho';
type Rascunho = { dia: string; destino: string; atividade: number | null; texto: string; qtd: string; comHorario: boolean; hIni: string; hFim: string };
function lerRascunho(): Rascunho | null {
  try {
    const v = window.localStorage.getItem(CHAVE_RASCUNHO);
    const r = v ? JSON.parse(v) : null;
    return r && r.dia === dayjs().format('YYYY-MM-DD') ? r : null;
  } catch { return null; }
}
function gravarRascunho(r: Rascunho) {
  try { window.localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(r)); } catch { /* sem armazenamento */ }
}

const campo: React.CSSProperties = {
  width: '100%', fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d9d9d9',
  background: '#fff', boxSizing: 'border-box',
};
const botao = (cor: string, ativo = true): React.CSSProperties => ({
  background: cor, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px',
  fontSize: 12, fontWeight: 600, cursor: ativo ? 'pointer' : 'default', opacity: ativo ? 1 : 0.6,
});
const botaoLeve: React.CSSProperties = {
  background: 'none', border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 10px',
  fontSize: 11, color: '#333', cursor: 'pointer',
};
const link: React.CSSProperties = { background: 'none', border: 'none', color: AZUL, fontSize: 11, cursor: 'pointer', padding: 0 };

export default function HorasAtalho({ onAbrirSemana }: { onAbrirSemana: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [hoje, setHoje] = useState<string>('');
  const [aps, setAps] = useState<Ap[]>([]);
  const [dia, setDia] = useState<Dia | null>(null);
  const [cron, setCron] = useState<Cron | null>(null);
  const [relogio, setRelogio] = useState<{ agora: string; minuto: number; recebido: number } | null>(null);
  const [horasDia, setHorasDia] = useState(8);
  // Rateio fixo no cadastro (01/10/2026): a pessoa não deve anotação — sem ponto laranja.
  const [rateioFixo, setRateioFixo] = useState(false);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [ccs, setCcs] = useState<CC[]>([]);
  const [catalogo, setCatalogo] = useState<AtividadeTipo[]>([]);
  const [rascunho] = useState(lerRascunho);
  const [destino, setDestino] = useState(rascunho?.destino || '');
  const [atividade, setAtividade] = useState<number | null>(rascunho?.atividade ?? null);
  const [texto, setTexto] = useState(rascunho?.texto || '');
  const [qtd, setQtd] = useState(rascunho?.qtd || '');
  const [comHorario, setComHorario] = useState(!!rascunho?.comHorario);
  const [hIni, setHIni] = useState(rascunho?.hIni || '');
  const [hFim, setHFim] = useState(rascunho?.hFim || '');
  useEffect(() => {
    gravarRascunho({ dia: dayjs().format('YYYY-MM-DD'), destino, atividade, texto, qtd, comHorario, hIni, hFim });
  }, [destino, atividade, texto, qtd, comHorario, hIni, hFim]);
  const [msg, setMsg] = useState<{ tipo: 'erro' | 'ok'; txt: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, setConfirmar] = useState<string | null>(null);   // id armado para apagar / 'descartar'
  const [somar, setSomar] = useState<{ texto: string; seguir: () => void } | null>(null); // hora contada 2x?
  const [pergunta, setPergunta] = useState<PerguntaFim | null>(null);
  const [ajuste, setAjuste] = useState<{ data: string; hora: string } | null>(null);
  const [, setTique] = useState(0);
  const [estreito, setEstreito] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 480 : false));
  const caixa = useRef<HTMLDivElement>(null);

  const avisar = (tipo: 'erro' | 'ok', txt: string) => {
    setMsg({ tipo, txt });
    setTimeout(() => setMsg(m => (m && m.txt === txt ? null : m)), 6000);
  };

  // A semana sai do "hoje" do SERVIDOR (SP). O relógio do computador só é
  // usado na primeira vez; se ele estiver noutro fuso e cair na semana errada,
  // relê na certa.
  const hojeConhecido = useRef<string | null>(null);
  const carregar = useCallback(async () => {
    try {
      let ini = hojeConhecido.current ? segundaDeIso(hojeConhecido.current) : segundaDe(dayjs()).format('YYYY-MM-DD');
      let d = await req(`/financeiro/horas/semana?inicio=${ini}`);
      if (d.hoje && segundaDeIso(d.hoje) !== ini) {
        ini = segundaDeIso(d.hoje);
        d = await req(`/financeiro/horas/semana?inicio=${ini}`);
      }
      hojeConhecido.current = d.hoje;
      setHoje(d.hoje);
      setAps((d.apontamentos || []).filter((a: Ap) => a.data === d.hoje));
      setDia((d.dias || []).find((x: Dia) => x.data === d.hoje) || null);
      setCron(d.cronometro || null);
      setHorasDia(Number(d.horas_dia) || 8);
      setRateioFixo(!!d.rateio_fixo);
      if (d.agora) setRelogio({ agora: d.agora, minuto: Number(d.agora_minuto) || 0, recebido: Date.now() });
    } catch { /* atalho não incomoda com erro de rede; a tela cheia mostra */ }
  }, []);

  // Ao montar, de 10 em 10 minutos, e ao VOLTAR para a aba: outra aba pode
  // ter ligado ou parado o cronômetro.
  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 10 * 60 * 1000);
    const voltou = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', voltou);
    window.addEventListener('focus', voltou);
    window.addEventListener(EVENTO_HORAS, carregar);
    const abrir = () => setAberto(true);
    window.addEventListener(EVENTO_ABRIR_ATALHO, abrir);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', voltou);
      window.removeEventListener('focus', voltou);
      window.removeEventListener(EVENTO_HORAS, carregar);
      window.removeEventListener(EVENTO_ABRIR_ATALHO, abrir);
    };
  }, [carregar]);

  // O relógio do cronômetro anda sozinho (sem ir ao servidor).
  useEffect(() => {
    if (!cron) return;
    const t = setInterval(() => setTique(x => x + 1), 20 * 1000);
    return () => clearInterval(t);
  }, [cron]);

  useEffect(() => {
    const f = () => setEstreito(window.innerWidth < 480);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);

  // Contratos e lista de atividades só quando abre pela primeira vez: quem
  // nunca abrir o atalho não paga por elas.
  useEffect(() => {
    if (!aberto || contratos.length || ccs.length) return;
    (async () => {
      try {
        const d = await req('/financeiro/horas/contratos');
        setContratos(d.contratos || []);
        setCcs(d.centros_custo || []);
      } catch { /* silencioso */ }
      try { setCatalogo((await req('/financeiro/horas/tipos-atividade')).atividades || []); } catch { /* texto livre segue */ }
    })();
  }, [aberto, contratos.length, ccs.length]);

  useEffect(() => { if (aberto) carregar(); }, [aberto, carregar]);

  // O destino que existe nas opções carregadas. O último uso pode ter saído
  // da lista (contrato cancelado): restaurá-lo escondido no <select> mandaria
  // a hora para lá sem a pessoa ver.
  const existeOpcao = useCallback((d: string) => (d.startsWith('c:')
    ? contratos.some(c => c.id === d.slice(2))
    : ccs.some(c => c.codigo_canonical === d.slice(2))), [contratos, ccs]);
  useEffect(() => {
    if (!(contratos.length || ccs.length)) return;
    // O rascunho também pode trazer um destino que saiu da lista.
    if (destino && !existeOpcao(destino)) { setDestino(''); return; }
    if (destino) return;
    const u = lerUltimo();
    if (u && u.destino && existeOpcao(u.destino)) setDestino(u.destino);
  }, [contratos.length, ccs.length, destino, existeOpcao]);

  // Fechar ao clicar fora.
  //
  // Pelo CAMINHO do evento, não pelo alvo: escolher uma atividade da lista
  // (ou um destino) tira a lista da tela no próprio mousedown, e quando este
  // ouvinte roda o alvo já está solto do documento — `contains` dizia "fora"
  // e o atalho fechava na cara da pessoa (Lucas, 28/09). O caminho é gravado
  // quando o evento nasce.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      const dentro = caixa.current && e.composedPath().includes(caixa.current);
      if (!dentro) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const totalHoje = aps.reduce((s, a) => s + a.minutos, 0);
  const estado = estadoDoDia(dia?.status, totalHoje);
  const ausencia = dia?.status === 'ausencia';
  const cor = estado === 'pendente' && !rateioFixo ? LARANJA : null;
  const rotulo = (a: { contrato_numero: string | null; centro_custo_nome: string | null }) =>
    a.contrato_numero || a.centro_custo_nome || '—';

  const minCron = cron && relogio ? decorrido(cron.inicio, relogio.agora, relogio.recebido, Date.now()) : 0;
  // Quanto do cronômetro cai HOJE (se começou ontem, desde a meia-noite).
  const agoraHoje = relogio && hoje ? agoraSP(hoje, relogio.minuto, relogio.recebido, Date.now()) : null;
  const cronHoje = !cron ? 0 : cron.data_inicio === hoje ? minCron : (agoraHoje ? agoraHoje.minuto : 0);
  const doDiaHoje = aps.map(x => ({ minutos: x.minutos, comHorario: !!x.hora_inicio, faixa: x.hora_inicio ? `${x.hora_inicio}–${x.hora_fim}` : null }));
  // Ligado desde ontem, ou há mais de 10 h: quase sempre esquecido.
  const cronSuspeito = !!cron && (cron.data_inicio !== hoje || minCron > 600);

  const alvoDoDestino = (d: string) => (d.startsWith('c:') ? { contrato_id: d.slice(2) } : { centro_custo_codigo: d.slice(2) });
  const rotuloDoDestino = (d: string) => {
    if (d.startsWith('c:')) { const c = contratos.find(x => x.id === d.slice(2)); return c ? c.numero : 'contrato'; }
    const c = ccs.find(x => x.codigo_canonical === d.slice(2)); return c ? c.nome_completo : d.slice(2);
  };
  const lembrar = () => destino && gravarUltimo({
    destino, atividade, texto: texto.trim(),
    rotulo: `${rotuloDoDestino(destino)}${atividade ? ` · ${catalogo.find(a => a.id === atividade)?.nome || ''}` : ''}`,
  });

  // O servidor diz que o cronômetro mudou noutra aba: relê e explica.
  async function tratarErro(e: any, doAnterior = false) {
    const cod = e?.dados?.codigo;
    if (cod === 'cronometro_longo' || cod === 'cronometro_ausencia') {
      const cr = e.dados.cronometro || cron;
      const sug = e.dados.sugestao_fim || (cr ? { data: cr.data_inicio, hora: '18:00' } : { data: hoje, hora: '18:00' });
      if (e.dados.cronometro) setCron(e.dados.cronometro);
      setPergunta({
        texto: `${doAnterior ? 'Antes de começar outro: ' : ''}${e.message}`,
        data: sug.data, hora: sug.hora, confirmavel: !!e.dados.confirmavel,
      });
      return;
    }
    if (cod === 'cronometro_mudou') { await carregar(); avisar('erro', e.message); return; }
    avisar('erro', e.message);
  }

  // `de` = o "▶ Continuar" de um toque, com o último destino/atividade usados.
  async function iniciar(de?: Ultimo) {
    const dest = de ? de.destino : destino;
    const atv = de ? de.atividade : atividade;
    const txt = de ? de.texto : texto.trim();
    if (!dest) { avisar('erro', 'Escolha onde você está trabalhando.'); return; }
    setOcupado(true);
    try {
      const r = await req('/financeiro/horas/cronometro/iniciar', {
        method: 'POST',
        body: JSON.stringify({
          ...alvoDoDestino(dest), atividade_tipo_id: atv, descricao: txt || null,
          ...(cron ? { anterior_id: cron.id } : {}),
        }),
      });
      if (de) setDestino(de.destino); else lembrar();
      const g = r.anterior?.gravados || [];
      avisar('ok', g.length
        ? `Gravado ${g.map((x: any) => `${x.hora_inicio}–${x.hora_fim}`).join(' e ')} no anterior. Cronômetro ligado.`
        : 'Cronômetro ligado.');
      // Texto e vínculo foram para o cronômetro; o formulário volta limpo para
      // a atividade de um não grudar no próximo lançamento.
      setTexto(''); setAtividade(null);
      await carregar();
      avisarMudanca();
    } catch (e: any) { await tratarErro(e, !!cron); }
    finally { setOcupado(false); }
  }

  async function parar(extra: Record<string, unknown> = {}, confirmado = false): Promise<boolean> {
    if (!cron) return false;
    // Parar num dia que já tem horas SEM horário (o "8" de sempre): pergunta
    // antes de somar — é o outro lado da hora contada duas vezes.
    if (!confirmado && !Object.keys(extra).length && cron.data_inicio === hoje) {
      const q = perguntaDeSoma({ data: hoje, novos: minCron, novoComHorario: true, doDia: doDiaHoje, cronometroNoDia: 0, horasDia });
      if (q) {
        setSomar({ texto: q, seguir: () => { parar(extra, true); } });
        return false;
      }
    }
    setSomar(null);
    setOcupado(true);
    try {
      const r = await req('/financeiro/horas/cronometro/parar', {
        method: 'POST', body: JSON.stringify({ id: cron.id, ...extra }),
      });
      const g = r.gravados || [];
      setPergunta(null);
      avisar('ok', r.descartado
        ? 'Menos de 1 minuto — nada gravado.'
        : `Gravado ${g.map((x: any) => `${x.hora_inicio}–${x.hora_fim}`).join(' e ')} (${formatarHoras(g.reduce((s: number, x: any) => s + x.minutos, 0))}) em ${rotulo(cron)}.`);
      await carregar();
      avisarMudanca();
      return true;
    } catch (e: any) { await tratarErro(e); return false; }
    finally { setOcupado(false); }
  }

  async function corrigirDesdeInicio() {
    if (!cron) return;
    if (!destino) { avisar('erro', 'Escolha acima para onde o período inteiro deve ir.'); return; }
    setOcupado(true);
    try {
      await req('/financeiro/horas/cronometro', {
        method: 'PATCH',
        // Só o destino, mais o que a pessoa escreveu/escolheu AGORA: o campo
        // de texto está vazio desde o iniciar, e mandar null apagaria o "o que
        // você fez" do cronômetro.
        body: JSON.stringify({
          id: cron.id, ...alvoDoDestino(destino),
          ...(atividade !== null ? { atividade_tipo_id: atividade } : {}),
          ...(texto.trim() ? { descricao: texto.trim() } : {}),
        }),
      });
      lembrar();
      setTexto(''); setAtividade(null);
      avisarMudanca();
      avisar('ok', `Corrigido: desde ${cron.hora_inicio} conta para ${rotuloDoDestino(destino)}.`);
      await carregar();
    } catch (e: any) { await tratarErro(e); }
    finally { setOcupado(false); }
  }

  async function ajustarInicio() {
    if (!cron || !ajuste) return;
    const h = parseHorario(ajuste.hora);
    if (!('minutos' in h)) { avisar('erro', 'Horário não entendido. Use 08:30.'); return; }
    setOcupado(true);
    try {
      await req('/financeiro/horas/cronometro', {
        method: 'PATCH',
        body: JSON.stringify({ id: cron.id, inicio_data: ajuste.data, inicio_hora: formatarHorario(h.minutos) }),
      });
      setAjuste(null);
      avisar('ok', `Início ajustado para ${formatarHorario(h.minutos)}.`);
      await carregar();
    } catch (e: any) { await tratarErro(e); }
    finally { setOcupado(false); }
  }

  async function descartar() {
    if (!cron) return;
    if (confirmar !== 'descartar') {
      setConfirmar('descartar');
      setTimeout(() => setConfirmar(c => (c === 'descartar' ? null : c)), 4000);
      return;
    }
    setConfirmar(null);
    setOcupado(true);
    try {
      const r = await req(`/financeiro/horas/cronometro?id=${encodeURIComponent(cron.id)}`, { method: 'DELETE' });
      setPergunta(null);
      const g = r.gravados || [];
      avisar('ok', r.ja_parado
        ? (g.length
          ? `Esse cronômetro já tinha sido parado (noutra aba ou aparelho) e gravou ${g.map((x: any) => `${x.hora_inicio}–${x.hora_fim}`).join(' e ')}.`
          : 'Esse cronômetro já não estava ligado.')
        : 'Cronômetro descartado — nada foi gravado.');
      await carregar();
      avisarMudanca();
    } catch (e: any) { await tratarErro(e); }
    finally { setOcupado(false); }
  }

  async function lancar(confirmado = false) {
    if (!destino) { avisar('erro', 'Escolha onde você trabalhou.'); return; }
    const corpo: any = { ...alvoDoDestino(destino), data: hoje, atividade_tipo_id: atividade, descricao: texto.trim() || null };
    let minutos = 0; let interpretado: string | undefined;
    if (comHorario) {
      const a = parseHorario(hIni); const b = parseHorario(hFim, { permitir24: true });
      if (!('minutos' in a) || !('minutos' in b)) {
        avisar('erro', 'erro' in a ? a.erro : 'erro' in b ? b.erro : 'Diga de que horas a que horas.');
        return;
      }
      const d = duracaoEntre(a.minutos, b.minutos);
      if ('erro' in d) { avisar('erro', d.erro); return; }
      corpo.hora_inicio = formatarHorario(a.minutos); corpo.hora_fim = formatarHorario(b.minutos);
      minutos = d.minutos;
    } else {
      const r = parseEntradaHoras(qtd);
      if ('erro' in r) { avisar('erro', r.erro); return; }
      if (!r.minutos) { avisar('erro', 'Quantas horas?'); return; }
      corpo.minutos = r.minutos; minutos = r.minutos; interpretado = r.interpretado;
    }
    // Hora contada duas vezes (cronômetro correndo de manhã + "8" à tarde, ou
    // o contrário): a mesma régua da grade e da janela, com o cronômetro junto.
    if (!confirmado) {
      const q = perguntaDeSoma({ data: hoje, novos: minutos, novoComHorario: comHorario, doDia: doDiaHoje, cronometroNoDia: cronHoje, horasDia });
      if (q) { setSomar({ texto: q, seguir: () => { lancar(true); } }); return; }
    }
    setSomar(null);
    setOcupado(true);
    try {
      await req('/financeiro/horas', { method: 'POST', body: JSON.stringify(corpo) });
      lembrar();
      setQtd(''); setHIni(''); setHFim(''); setTexto(''); setAtividade(null);
      avisar('ok', interpretado ? `Lançado — ${interpretado}` : `Lançado ${formatarHoras(minutos)}`);
      await carregar();
      avisarMudanca();
    } catch (e: any) { await tratarErro(e); }
    finally { setOcupado(false); }
  }

  // Apagar em dois toques, sem caixa de diálogo: num menu pequeno, um modal de
  // confirmação por cima seria mais atrito do que o erro que ele evita. O
  // primeiro clique arma e mostra "apagar?"; o segundo apaga. Sai sozinho em
  // 4 s para não ficar armado sem a pessoa perceber.
  async function apagar(a: Ap) {
    if (confirmar !== a.id) {
      setConfirmar(a.id);
      setTimeout(() => setConfirmar(c => (c === a.id ? null : c)), 4000);
      return;
    }
    setConfirmar(null);
    setOcupado(true);
    try {
      await req(`/financeiro/horas/${a.id}`, { method: 'DELETE' });
      avisar('ok', `${formatarHoras(a.minutos)} apagadas`);
      await carregar();
      avisarMudanca();
    } catch (e: any) { avisar('erro', e.message); }
    finally { setOcupado(false); }
  }

  // Home office / campo de hoje (30/09/2026). A caixa responde na hora e o
  // servidor confirma depois; a releitura (pelo aviso, que este atalho também
  // escuta) só vem quando o ÚLTIMO clique em voo volta — reler entre dois
  // cliques seguidos faria a segunda caixa piscar desmarcada.
  const marcasEmVoo = useRef(0);
  async function marcarLocal(marca: MarcaLocal, valor: boolean) {
    if (!hoje) return;
    setDia(d => ({ ...(d || { data: hoje, status: 'aberto', tipo_ausencia: null, minutos_total: 0 }), [marca]: valor }));
    marcasEmVoo.current += 1;
    try {
      await req(`/financeiro/horas/dia/${hoje}/local`, { method: 'POST', body: JSON.stringify({ [marca]: valor }) });
    } catch (e: any) { avisar('erro', e.message); }
    finally {
      marcasEmVoo.current -= 1;
      if (marcasEmVoo.current === 0) avisarMudanca();
    }
  }

  const ultimoLido = lerUltimo();
  const ultimo = ultimoLido && ultimoLido.destino && existeOpcao(ultimoLido.destino) ? ultimoLido : null;
  const gruposDestino = montarOpcoesDestino(contratos, ccs);

  const painel: React.CSSProperties = estreito
    ? { position: 'fixed', top: 60, left: 12, right: 12 }
    : { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340 };

  return (
    <div ref={caixa} style={{ position: 'relative' }}>
      <button
        onClick={() => setAberto(v => !v)}
        title={cron
          ? `Cronômetro ligado desde ${cron.data_inicio !== hoje ? `${diaDaSemana(cron.data_inicio)} ` : ''}${cron.hora_inicio} em ${rotulo(cron)}`
          : totalHoje > 0
            ? `${formatarHoras(totalHoje)} anotadas hoje`
            : 'Você ainda não anotou nada hoje'}
        aria-label="Anotar horas"
        style={{
          position: 'relative', background: aberto ? '#F0F2F8' : cronSuspeito ? '#FFF4E5' : 'transparent',
          border: 'none', cursor: 'pointer', padding: 7, borderRadius: 8,
          color: '#020D7F', display: 'flex', alignItems: 'center', gap: 4, transition: 'background 0.12s',
        }}
        onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = '#F0F2F8')}
        onMouseLeave={e => { if (!aberto) (e.currentTarget as HTMLElement).style.background = cronSuspeito ? '#FFF4E5' : 'transparent'; }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        {cron ? (
          <span style={{ fontSize: 12, fontWeight: 600, color: cronSuspeito ? LARANJA : VERMELHO, display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: cronSuspeito ? LARANJA : VERMELHO }} />
            {cron.data_inicio !== hoje ? 'desde ontem' : formatarRelogio(minCron)}
          </span>
        ) : cor && (
          <span style={{
            position: 'absolute', top: 4, right: 4, width: 8, height: 8,
            borderRadius: '50%', background: cor, border: '1.5px solid #fff',
          }} />
        )}
      </button>

      {aberto && (
        <div style={{
          ...painel, background: '#fff', borderRadius: 10, zIndex: 300,
          boxShadow: '0 8px 28px rgba(9,48,208,0.16)', border: '0.5px solid #E6E8F0',
          overflow: 'hidden', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 13, color: '#020D7F' }}>
              Hoje, {hoje ? `${diaDaSemana(hoje)} ${hoje.slice(8, 10)}/${hoje.slice(5, 7)}` : '—'}
            </strong>
            <span style={{ fontSize: 12, color: totalHoje > 0 ? '#222' : '#999' }}>
              <TotalHoras minutos={totalHoje} alinhar="end" tamanhoDecimal={10} />
            </span>
          </div>

          {/* Onde estou hoje: duas caixas independentes; nenhuma = escritório. */}
          {hoje && !ausencia && (
            <div style={{ padding: '7px 14px', borderBottom: '0.5px solid #f0f0f0',
                          display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#333' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                title="Estou trabalhando de casa ou remoto hoje">
                <input type="checkbox" checked={!!dia?.remoto} style={{ accentColor: AZUL, margin: 0 }}
                  onChange={e => marcarLocal('remoto', e.target.checked)} />
                Home office / remoto
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                title="Fui a campo hoje">
                <input type="checkbox" checked={!!dia?.campo} style={{ accentColor: AZUL, margin: 0 }}
                  onChange={e => marcarLocal('campo', e.target.checked)} />
                Fui a campo
              </label>
            </div>
          )}

          {/* ── Cronômetro ligado: SEMPRE visível ─────────────────────────── */}
          {cron && (
            <div style={{ padding: '10px 14px', background: cronSuspeito ? '#FFF7EC' : '#FFF5F4', borderBottom: '0.5px solid #f3e0de' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ⏱ <strong>{rotulo(cron)}</strong>{cron.atividade_tipo_nome ? ` · ${cron.atividade_tipo_nome}` : ''}
                </span>
                <strong style={{ fontSize: 16, color: cronSuspeito ? LARANJA : VERMELHO, fontVariantNumeric: 'tabular-nums' }}>
                  {formatarRelogio(minCron)}
                </strong>
              </div>
              {cron.descricao && cron.descricao !== cron.atividade_tipo_nome && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{cron.descricao}</div>
              )}
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                desde {cron.data_inicio !== hoje ? `${diaDaSemana(cron.data_inicio)} ${cron.data_inicio.slice(8, 10)}/${cron.data_inicio.slice(5, 7)} às ` : ''}{cron.hora_inicio}{' '}
                <button style={link} onClick={() => setAjuste(a => (a ? null : { data: cron.data_inicio, hora: cron.hora_inicio }))}>
                  {ajuste ? 'cancelar' : 'ajustar'}
                </button>
              </div>
              {ajuste && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#555' }}>Comecei</span>
                  <input type="date" value={ajuste.data} max={hoje} onChange={e => setAjuste({ ...ajuste, data: e.target.value })}
                    style={{ ...campo, width: 128 }} />
                  <input value={ajuste.hora} onChange={e => setAjuste({ ...ajuste, hora: e.target.value })}
                    placeholder="08:30" style={{ ...campo, width: 64 }} />
                  <button style={botao(AZUL, !ocupado)} disabled={ocupado} onClick={ajustarInicio}>OK</button>
                </div>
              )}

              {pergunta ? (
                <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 6, border: '1px solid #F2C18D' }}>
                  <div style={{ fontSize: 12, color: '#7A4A00', marginBottom: 6 }}>{pergunta.texto}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#555' }}>Parei em</span>
                    <input type="date" value={pergunta.data} max={hoje} min={cron.data_inicio}
                      onChange={e => setPergunta({ ...pergunta, data: e.target.value })} style={{ ...campo, width: 128 }} />
                    <span style={{ fontSize: 11, color: '#555' }}>às</span>
                    <input value={pergunta.hora} onChange={e => setPergunta({ ...pergunta, hora: e.target.value })}
                      placeholder="18:00" style={{ ...campo, width: 64 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <button style={botao(AZUL, !ocupado)} disabled={ocupado} onClick={() => {
                      const h = parseHorario(pergunta.hora, { permitir24: true });
                      if (!('minutos' in h)) { avisar('erro', 'Horário não entendido. Use 18:30.'); return; }
                      parar({ fim_data: pergunta.data, fim_hora: formatarHorario(h.minutos) });
                    }}>Gravar</button>
                    {pergunta.confirmavel && (
                      <button style={botaoLeve} disabled={ocupado} onClick={() => parar({ confirmar_longo: true })}>
                        Trabalhei até agora
                      </button>
                    )}
                    <button style={botaoLeve} onClick={() => setPergunta(null)}>Voltar</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                  <button style={botao(VERMELHO, !ocupado)} disabled={ocupado} onClick={() => parar()}>⏹ Parar</button>
                  <button
                    onClick={descartar} disabled={ocupado}
                    title="Desligar sem gravar nada"
                    style={{ ...botaoLeve, background: confirmar === 'descartar' ? '#c0392b' : 'none',
                             color: confirmar === 'descartar' ? '#fff' : '#666', border: confirmar === 'descartar' ? 'none' : botaoLeve.border }}>
                    {confirmar === 'descartar' ? 'descartar mesmo?' : 'descartar'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Lançamentos de hoje ─────────────────────────────────────── */}
          {aps.length > 0 && (
            <div style={{ maxHeight: 132, overflowY: 'auto' }}>
              {aps.map(a => (
                <div key={a.id} style={{
                  padding: '7px 14px', display: 'flex', justifyContent: 'space-between',
                  gap: 8, fontSize: 12, borderBottom: '0.5px solid #f7f7f7', alignItems: 'center',
                }}>
                  <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rotulo(a)}
                    {a.hora_inicio && <span style={{ color: '#888' }}> · {a.hora_inicio}–{a.hora_fim}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <span style={{ color: '#666' }}>{formatarHoras(a.minutos)}</span>
                    {/* Listar sem deixar apagar é mostrar o erro e trancar a
                        porta. Confirmação em dois toques. */}
                    <button
                      onClick={() => apagar(a)}
                      title={confirmar === a.id ? 'Clique de novo para apagar' : 'Apagar este lançamento'}
                      style={{
                        background: confirmar === a.id ? '#c0392b' : 'none',
                        color: confirmar === a.id ? '#fff' : '#bbb',
                        border: 'none', borderRadius: 4, cursor: 'pointer',
                        fontSize: 11, lineHeight: 1, padding: confirmar === a.id ? '3px 6px' : '3px 4px',
                      }}>
                      {confirmar === a.id ? 'apagar?' : '✕'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {ausencia ? (
            <div style={{ padding: '14px', fontSize: 12, color: '#555' }}>
              Dia marcado como <strong>{dia?.tipo_ausencia}</strong>. Nada a anotar.
            </div>
          ) : (
            <div style={{ padding: '10px 14px', borderTop: aps.length ? '0.5px solid #f0f0f0' : 'none' }}>
              {!cron && ultimo && (
                <button
                  onClick={() => iniciar(ultimo)} disabled={ocupado}
                  title="Liga o cronômetro no último contrato e atividade que você usou"
                  style={{ ...botaoLeve, width: '100%', marginBottom: 8, textAlign: 'left', color: AZUL, borderColor: '#C9D3F5',
                           overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ▶ Continuar {ultimo.rotulo}
                </button>
              )}
              <DestinoCampo compacto valor={destino} grupos={gruposDestino} onEscolher={setDestino} />
              <div style={{ marginTop: 6 }}>
                <AtividadeCampo compacto texto={texto} atividadeId={atividade} catalogo={catalogo}
                  onTexto={setTexto} onAtividade={setAtividade} />
              </div>

              {/* Cronômetro */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {!cron ? (
                  <button style={{ ...botao(AZUL, !ocupado), flex: 1 }} disabled={ocupado} onClick={() => iniciar()}>
                    ▶ Iniciar cronômetro
                  </button>
                ) : (
                  <>
                    <button style={{ ...botao(AZUL, !ocupado), flex: 1 }} disabled={ocupado} onClick={() => iniciar()}
                      title="Grava o cronômetro atual até agora e começa este">
                      ↻ Trocar a partir de agora
                    </button>
                    <button style={{ ...botaoLeve, flex: 1 }} disabled={ocupado} onClick={corrigirDesdeInicio}
                      title={`O período inteiro, desde ${cron.hora_inicio}, passa a contar para o que está escolhido acima`}>
                      ✎ Corrigir desde {cron.hora_inicio}
                    </button>
                  </>
                )}
              </div>

              {/* Anotar direto */}
              <>
                  <div style={{ fontSize: 11, color: '#999', margin: '10px 0 6px', textAlign: 'center' }}>ou anote direto</div>
                  {comHorario ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#555' }}>das</span>
                      <input value={hIni} onChange={e => setHIni(e.target.value)} placeholder="08:00" style={{ ...campo, width: 62 }} />
                      <span style={{ fontSize: 11, color: '#555' }}>às</span>
                      <input value={hFim} onChange={e => setHFim(e.target.value)} placeholder="12:00"
                        onKeyDown={e => { if (e.key === 'Enter') lancar(); }} style={{ ...campo, width: 62 }} />
                      <button onClick={() => lancar()} disabled={ocupado} style={{ ...botao(AZUL, !ocupado), marginLeft: 'auto' }}>Anotar</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={qtd} onChange={e => setQtd(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') lancar(); }}
                        placeholder="2 · 1,5 · 1:30 · 90m" style={{ ...campo, flex: 1, minWidth: 0 }} />
                      <button onClick={() => lancar()} disabled={ocupado} style={botao(AZUL, !ocupado)}>Anotar</button>
                    </div>
                  )}
                  <button style={{ ...link, marginTop: 6 }} onClick={() => setComHorario(v => !v)}>
                    {comHorario ? 'só a quantidade' : 'com horário (das __ às __)'}
                  </button>
              </>
            </div>
          )}

          {/* Hora contada duas vezes? Vale para o "anote direto" e para o Parar. */}
          {somar && (
            <div style={{ margin: '0 14px 8px', padding: 8, background: '#FFF7EC', borderRadius: 6, fontSize: 12, color: '#7A4A00' }}>
              {somar.texto}
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button style={botao(AZUL, !ocupado)} disabled={ocupado} onClick={() => somar.seguir()}>Somar</button>
                <button style={botaoLeve} onClick={() => setSomar(null)}>Corrigir</button>
              </div>
            </div>
          )}

          {msg && (
            <div style={{
              padding: '6px 14px', fontSize: 11,
              color: msg.tipo === 'erro' ? '#c0392b' : '#207544',
              background: msg.tipo === 'erro' ? '#FFF3F2' : '#F2FBF5',
            }}>{msg.txt}</div>
          )}

          <div style={{
            padding: '8px 14px', borderTop: '0.5px solid #f0f0f0', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', gap: 8,
          }}>
            <button onClick={() => { setAberto(false); onAbrirSemana(); }} style={{ ...link, fontSize: 12 }}>
              Ver a semana
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
